import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { InlineCta } from "@/components/site/InlineCta";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";
import { GuideLinks } from "../../components/site/Guides";
import { KnowledgeHubNote } from "../../components/site/KnowledgeHubNote";
import { NewcomerNote } from "../../components/site/NewcomerNote";

const title = "Schools in Marbella and Estepona – a guide";
const description =
  "A local guide to schooling between Marbella and Estepona: areas, commuting, school options and what to consider when choosing where to live.";

export const Route = createFileRoute("/en/schools-in-marbella-and-estepona")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/schools-in-marbella-and-estepona") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/schools-in-marbella-and-estepona"),
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        eyebrow="Guide"
        title="Schools in Marbella and Estepona"
        intro="The coastal stretch between Estepona and Marbella is one of the areas in Spain where the most Swedish families settle. Here's a rundown of how choosing a school ties in with where you choose to live."
      />

      <NewcomerNote />

      <Prose>
        <h2>A spread-out area</h2>
        <p>
          From Estepona in the west to Marbella in the east is a few dozen kilometres along the
          coast, with residential areas, urbanisations and small towns the whole way. There's no
          single clear centre where everything is gathered – everyday life is spread out, and most
          things are done by car. That affects the choice of school more than many expect: a school
          that looks close on the map can mean a noticeable journey time morning and afternoon when
          traffic is heaviest.
        </p>
        <p>
          Our school is located in Cancelada, between Estepona and Marbella, which means families
          from both directions can reach it without crossing the whole area. For many families, that
          in-between location is exactly what makes everyday life practically manageable.
        </p>

        <h2>Areas Swedish families often consider</h2>
        <ul>
          <li>
            <strong>Estepona:</strong> more of a distinctly Spanish town feel, plenty of amenities
            and a somewhat calmer pace.
          </li>
          <li>
            <strong>Cancelada and Costalita:</strong> residential areas midway between the towns,
            close to the school and the coast.
          </li>
          <li>
            <strong>New Golden Mile and the Benahavís side:</strong> villa areas and
            urbanisations, often with golf and padel nearby.
          </li>
          <li>
            <strong>San Pedro and Nueva Andalucía:</strong> a wide range of activities and
            amenities, somewhat closer to Marbella.
          </li>
          <li>
            <strong>Marbella:</strong> the widest range of amenities, but also the most traffic in
            and out morning and afternoon.
          </li>
        </ul>

        <h2>Commuting – time it at rush hour</h2>
        <p>
          Common advice from families already living here is to drive the route between your
          intended home and the school at eight in the morning before signing a rental agreement.
          The coast road and the motorway behave very differently depending on time of day and
          season. Think about the afternoons too: if the children have training or attend the
          leisure club, pick-up becomes part of the daily logistics.
        </p>
      </Prose>

      <div className="mx-auto max-w-3xl px-5">
        <InlineCta locale="en" />
      </div>

      <Prose>

        <h2>What types of school are there in the area?</h2>
        <p>
          The options mainly consist of Spanish state and private schools, international schools
          teaching in English, and a smaller number of schools with a different national profile –
          including Swedish. The choice is rarely about which school is best in general, but about
          which system suits your family's situation.
        </p>
        <p>
          If the plan is to stay long-term and put down local roots, a Spanish school has a lot
          going for it. If the horizon is uncertain or tied to work that might move you on,
          continuity in the curriculum matters more. A Swedish school lets children return to a
          Swedish class without losing pace, while studying Spanish and English every day.
        </p>

        <h2>What sets a small school apart from a large one?</h2>
        <p>
          The international and Spanish schools in the area are often large. A small school has
          fewer pupils per group, meaning staff know all the children and new pupils find their
          place in the community more quickly. There's also a downside to be aware of: fewer pupils
          means fewer parallel classes and sometimes mixed-age elements. For many families that's an
          advantage, but it's worth discussing what suits your particular child. Ask the school to
          show how its values shape daily life: how people treat one another, how conflicts are
          handled and how each child is followed up. That reveals more than polished words or pupil
          numbers alone.
        </p>

        <h2>Sport alongside school</h2>
        <p>
          The climate means outdoor sport can be practised year-round, and the area has an
          unusually strong offering in football, padel, tennis and golf. With us, it's possible to
          combine school with{" "}
          <Link className="text-primary link-quiet" to="/en/football-academy">
            Malmö FF's football academy
          </Link>{" "}
          or the{" "}
          <Link className="text-primary link-quiet" to="/en/padel-tennis">
            padel and tennis pathway
          </Link>
          , which is one reason families seek us out specifically.
        </p>

        <h2>Visit before you decide</h2>
        <p>
          Brochures and websites only tell you so much about how a school feels. Book a visit, walk
          around during a normal school day, see the premises and talk to staff. Bring the children
          along if you can – their first impressions tend to be more accurate than you'd think.
        </p>
        <p>
          If you'd like to come and see us, contact the headteacher and we'll arrange a visit or a
          digital meeting. Applications are accepted on a rolling basis, and it's fine to start
          mid-term.
        </p>
      </Prose>

      <KnowledgeHubNote resource="vardagen-i-spanien" locale="en" />

      <GuideLinks current="/skola-i-marbella-och-estepona" locale="en" />
      <CtaBand
        title="Ready to take the next step?"
        text="Applications are accepted on a rolling basis, and it's fine to start mid-term."
      />
      <div className="h-20" />
    </>
  );
}
